<template>
  <section class="form-section">
    <div class="section-header">
      <h2 class="section-title">Education</h2>
      <ContextMenu
        :is-authenticated="isAuthenticated"
        add-new-label="Add New Education"
        add-from-master-label="Add from Profile"
        @add-new="addEducation"
        @add-from-master="handleAddFromMaster"
      />
    </div>
    
    <div v-for="(education, index) in educationList" :key="index" class="form-card">
      <div class="card-header">
        <h3 class="card-title">Education {{ index + 1 }}</h3>
        <div class="flex items-center space-x-1 sm:space-x-2">
          <button
            v-if="isAuthenticated && isEntrySyncable(education)"
            type="button"
            @click.stop="$emit('sync-entry', { entry: education, entryType: 'education' })"
            class="flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#000537]/25 text-[#000537] dark:border-indigo-400/40 dark:text-indigo-400 text-[10px] font-medium hover:bg-[#000537]/5 dark:hover:bg-indigo-400/10 transition-colors"
          >
            <svg class="w-2.5 h-2.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"/>
            </svg>
            Save to profile
          </button>
          <button
            type="button"
            @click="moveEducationUp(index)"
            class="btn-reorder"
            v-if="index > 0"
            title="Move up"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
            </svg>
          </button>
          <button
            type="button"
            @click="moveEducationDown(index)"
            class="btn-reorder"
            v-if="index < educationList.length - 1"
            title="Move down"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <button type="button" @click="removeEducation(index)" class="btn-remove" v-if="educationList.length > 1" title="Delete">
            <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">Institution Name</label>
          <input 
            type="text" 
            :value="education.institution"
            @input="updateEducationField(index, 'institution', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'institution') }]"
            placeholder="London School of Economics"
            required
          />
          <div v-if="isFieldHighlighted(index, 'institution')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Location</label>
          <input 
            type="text" 
            :value="education.location"
            @input="updateEducationField(index, 'location', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'location') }]"
            placeholder="London, UK"
            required
          />
          <div v-if="isFieldHighlighted(index, 'location')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Start Date</label>
          <div class="flex space-x-1 sm:space-x-2">
            <select
              :value="formatMonth(education.startMonth)"
              @change="updateEducationField(index, 'startMonth', $event.target.value)"
              class="form-input flex-1"
              required
            >
              <option value="">Month</option>
              <option value="01">Jan</option>
              <option value="02">Feb</option>
              <option value="03">Mar</option>
              <option value="04">Apr</option>
              <option value="05">May</option>
              <option value="06">Jun</option>
              <option value="07">Jul</option>
              <option value="08">Aug</option>
              <option value="09">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
            <input 
              type="number" 
              :value="education.startYear" 
              @input="updateEducationField(index, 'startYear', $event.target.value)"
              class="form-input flex-1" 
              placeholder="2024"
              min="1950" 
              max="2030"
              required
            />
          </div>
        </div>
        <div class="form-group">
          <div class="flex items-start sm:items-center justify-between mb-1 flex-col sm:flex-row space-y-1 sm:space-y-0">
            <label class="form-label mb-0">End Date</label>
            <div class="flex items-center">
              <input 
                type="checkbox" 
                :id="`education-current-${index}`" 
                :checked="education.isCurrent"
                @change="updateEducationField(index, 'isCurrent', $event.target.checked)"
                class="currently-checkbox"
              />
              <label :for="`education-current-${index}`" class="currently-label">
                Currently studying
              </label>
            </div>
          </div>
          <div v-if="!education.isCurrent" class="flex space-x-1 sm:space-x-2">
            <select
              :value="formatMonth(education.endMonth)"
              @change="updateEducationField(index, 'endMonth', $event.target.value)"
              class="form-input flex-1"
              required
            >
              <option value="">Month</option>
              <option value="01">Jan</option>
              <option value="02">Feb</option>
              <option value="03">Mar</option>
              <option value="04">Apr</option>
              <option value="05">May</option>
              <option value="06">Jun</option>
              <option value="07">Jul</option>
              <option value="08">Aug</option>
              <option value="09">Sep</option>
              <option value="10">Oct</option>
              <option value="11">Nov</option>
              <option value="12">Dec</option>
            </select>
            <input 
              type="number" 
              :value="education.endYear" 
              @input="updateEducationField(index, 'endYear', $event.target.value)"
              class="form-input flex-1" 
              placeholder="2024"
              min="1950" 
              max="2030"
              required
            />
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Program Name</label>
          <input 
            type="text" 
            :value="education.programName"
            @input="updateEducationField(index, 'programName', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'programName') }]"
            placeholder="Financial Economics"
            required
          />
          <div v-if="isFieldHighlighted(index, 'programName')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Program Type</label>
          <input 
            type="text" 
            :value="education.programType"
            @input="updateEducationField(index, 'programType', $event.target.value)"
            class="form-input"
            placeholder="Master of Science"
            required
          />
        </div>
        <div class="form-group">
          <label class="form-label">GPA</label>
          <input 
            type="text" 
            :value="education.gpa"
            @input="updateEducationField(index, 'gpa', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'gpa') }]"
            placeholder="Current GPA: 1.4 (Top 6%)"
          />
          <div v-if="isFieldHighlighted(index, 'gpa')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group col-span-full">
          <div class="flex items-center justify-between">
            <label class="form-label">Description/Details</label>
            <BulletOptimizer
              v-if="isAuthenticated"
              entry-type="education"
              :bullets="education.bulletPoints"
              :context="{
                institution: education.institution,
                program_name: education.programName,
                program_type: education.programType,
                location: education.location
              }"
              :cv-entry-id="education.id || null"
              :master-entry-id="education.masterEducationEntryId || null"
              @apply="applyOptimizedBullets(index, $event)"
            />
          </div>
          <div class="space-y-1">
            <div
              v-for="(bulletPoint, bulletIndex) in education.bulletPoints"
              :key="`education-${index}-bullet-${bulletIndex}`"
              class="flex items-start space-x-1.5"
            >
              <span class="text-gray-400 dark:text-gray-500 text-xs mt-2 flex-shrink-0">•</span>
              <input
                type="text"
                :value="bulletPoint"
                @input="handleBulletInput(index, bulletIndex, $event.target.value)"
                :class="['form-input flex-1 min-w-0', { 'bullet-too-long': isBulletTooLong(bulletPoint) }]"
                :placeholder="bulletIndex === 0 ? 'Specialisation on Financial Markets & Corporate Finance' : 'Add another detail...'"
              />
              <span
                v-if="isBulletTooLong(bulletPoint)"
                class="bullet-length-hint mt-2 flex-shrink-0"
                title="Longer bullets may wrap to a second line in the PDF"
              >{{ bulletPoint.trim().length }}/{{ maxBulletChars }}</span>
              <button
                v-if="education.bulletPoints.length > 1 && bulletPoint.trim() !== ''"
                type="button"
                @click="removeBullet(index, bulletIndex)"
                class="text-gray-400 hover:text-red-500 transition-colors mt-2 flex-shrink-0"
                title="Remove bullet point"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Reference Documents Section (only for authenticated users) -->
      <ReferenceDocumentSection
        v-if="isAuthenticated"
        :entry-id="education.id || null"
        entry-type="education"
        :is-authenticated="isAuthenticated"
        @documents-updated="handleDocumentsUpdated"
      />
    </div>

    <!-- Master Data Modal -->
    <MasterDataModal 
      :is-open="showMasterDataModal"
      data-type="education"
      @close="closeMasterDataModal"
      @items-selected="handleMasterDataSelection"
    />
  </section>
</template>

<script>
import MasterDataModal from './MasterDataModal.vue'
import ContextMenu from './ContextMenu.vue'
import ReferenceDocumentSection from './ReferenceDocumentSection.vue'
import BulletOptimizer from './BulletOptimizer.vue'
import { CV_CONFIG } from '../config/cvConfig.js'

export default {
  name: 'EducationForm',
  components: {
    MasterDataModal,
    ContextMenu,
    ReferenceDocumentSection,
    BulletOptimizer
  },
  props: {
    educationList: {
      type: Array,
      required: true,
      default: () => [{
        institution: '',
        location: '',
        startMonth: '',
        startYear: '',
        endMonth: '',
        endYear: '',
        programName: '',
        programType: '',
        gpa: '',
        bulletPoints: [''],
        isCurrent: false
      }]
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    syncableIds: {
      type: Array,
      default: () => []
    },
    highlightedFields: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:educationList', 'field-changed', 'scroll-to-new', 'sync-entry'],
  data() {
    return {
      showMasterDataModal: false,
      maxBulletChars: CV_CONFIG.validation.maxBulletChars
    }
  },
  methods: {
    isBulletTooLong(bullet) {
      return (bullet || '').trim().length > this.maxBulletChars
    },

    isEntrySyncable(education) {
      if (!education.institution) return false
      if (!education.id) return true
      return this.syncableIds.includes(education.id)
    },

    formatMonth(m) {
      if (m === null || m === undefined || m === '') return ''
      return String(m).padStart(2, '0')
    },

    updateEducationField(index, field, value) {
      const updatedList = [...this.educationList]
      updatedList[index] = { ...updatedList[index], [field]: value }
      this.$emit('update:educationList', updatedList)
      this.$emit('field-changed')
    },

    addEducation() {
      const updatedList = [...this.educationList, {
        institution: '',
        location: '',
        startMonth: '',
        startYear: '',
        endMonth: '',
        endYear: '',
        programName: '',
        programType: '',
        gpa: '',
        bulletPoints: [''],
        isCurrent: false
      }]
      this.$emit('update:educationList', updatedList)
      this.$emit('field-changed')
      
      // Emit scroll event with new entry index
      this.$nextTick(() => {
        this.$emit('scroll-to-new', { type: 'education', index: updatedList.length - 1 })
      })
    },

    removeEducation(index) {
      if (this.educationList.length > 1) {
        const updatedList = [...this.educationList]
        updatedList.splice(index, 1)
        this.$emit('update:educationList', updatedList)
        this.$emit('field-changed')
      }
    },

    moveEducationUp(index) {
      if (index > 0) {
        const updatedList = [...this.educationList]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index - 1, 0, item)
        this.$emit('update:educationList', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'education', index: index - 1 })
        })
      }
    },

    moveEducationDown(index) {
      if (index < this.educationList.length - 1) {
        const updatedList = [...this.educationList]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index + 1, 0, item)
        this.$emit('update:educationList', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'education', index: index + 1 })
        })
      }
    },

    handleBulletInput(index, bulletIndex, value) {
      const updatedList = [...this.educationList]
      const education = { ...updatedList[index] }
      education.bulletPoints = [...education.bulletPoints]
      education.bulletPoints[bulletIndex] = value
      
      // Add new bullet point if this is the last one and it's not empty
      if (value.trim() !== '' && bulletIndex === education.bulletPoints.length - 1) {
        education.bulletPoints.push('')
      }
      
      // Remove empty bullet points (except the last one)
      for (let i = education.bulletPoints.length - 2; i >= 0; i--) {
        if (education.bulletPoints[i].trim() === '' && i !== education.bulletPoints.length - 1) {
          education.bulletPoints.splice(i, 1)
        }
      }
      
      updatedList[index] = education
      this.$emit('update:educationList', updatedList)
      this.$emit('field-changed')
    },

    removeBullet(index, bulletIndex) {
      const updatedList = [...this.educationList]
      const education = { ...updatedList[index] }

      if (education.bulletPoints.length > 1) {
        education.bulletPoints = [...education.bulletPoints]
        education.bulletPoints.splice(bulletIndex, 1)
        updatedList[index] = education
        this.$emit('update:educationList', updatedList)
        this.$emit('field-changed')
      }
    },

    applyOptimizedBullets(index, newBullets) {
      const updatedList = [...this.educationList]
      const education = { ...updatedList[index] }
      // Keep the trailing empty bullet so the "add another" input stays available
      education.bulletPoints = [...newBullets, '']
      updatedList[index] = education
      this.$emit('update:educationList', updatedList)
      this.$emit('field-changed')
    },

    handleAddFromMaster() {
      this.showMasterDataModal = true
    },

    handleMasterDataSelection(data) {
      // Convert master data items to education format
      const newEducationEntries = data.items.map(item => ({
        masterEducationEntryId: item.id || null,
        institution: item.institution || '',
        location: item.location || '',
        startMonth: item.start_month || '',
        startYear: item.start_year || '',
        endMonth: item.end_month || '',
        endYear: item.end_year || '',
        programName: item.program_name || '',
        programType: item.program_type || '',
        gpa: item.gpa || '',
        bulletPoints: item.base_bullet_points?.length ? item.base_bullet_points : [''],
        isCurrent: item.is_current || false
      }))

      // Add the new entries to the existing list
      const updatedList = [...this.educationList, ...newEducationEntries]
      this.$emit('update:educationList', updatedList)
      this.$emit('field-changed')

      // Scroll to the first new entry
      if (newEducationEntries.length > 0) {
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { 
            type: 'education', 
            index: this.educationList.length 
          })
        })
      }
    },

    closeMasterDataModal() {
      this.showMasterDataModal = false
    },

    isFieldHighlighted(index, fieldName) {
      const highlightedEntry = this.highlightedFields.find(entry => entry.index === index)
      return highlightedEntry && highlightedEntry.fields.includes(fieldName)
    },

    handleDocumentsUpdated(data) {
      // This method handles when reference documents are updated
      console.log(`Education entry ${data.entryId} now has ${data.documentCount} reference documents`)
      this.$emit('field-changed') // Trigger any auto-save or preview updates
    }
  }
}
</script>

<style scoped>
/* Field Highlighting Styles */
.field-needs-review {
  @apply border-2 border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20;
}

.field-needs-review:focus {
  @apply border-red-600 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-800;
}

.review-badge {
  @apply inline-flex items-center text-xs font-medium text-red-600;
}

/* Subtle warning for bullets that would wrap to a second line in the PDF */
.bullet-too-long {
  @apply border-amber-400 dark:border-amber-500;
}

.bullet-length-hint {
  @apply text-[10px] text-amber-500 dark:text-amber-400 tabular-nums;
}
</style>

<style scoped>
/* No additional styles needed - ContextMenu component handles all styling */
</style>